/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { Dispatch, SetStateAction, useMemo } from 'react';
import Image from 'next/image';
import { useLanguage } from '@/providers/LanguageProvider';
import ErrorBoundary from '@/utils/ErrorBoundray';
import { getTemplateLabelEnums } from '@/utils/evalTemplates';
import { formatDateMonthTime } from '@/utils/formatedDates';
import { sendEvalPromptToKahu } from '@/utils/sendEvalPromptToKahu';
import EvalBadge from '@/ux-common/src/components/evals/eval-badge';
import { iconMap } from '@/ux-common/src/lib/helper';
import { EvalResults } from '@/ux-common/src/types/api';
import { sizeStyles } from '@/ux-common/src/utils/constants';
import { IPrompt } from 'types/prompt';

import { formatSecondsAndMilliseconds, getRoleAndContent } from '@/lib/helper';
import { cn } from '@/lib/utils';
import { useEvals } from '@/hooks/useEvals';
import { Checkbox } from '@/components/ui/checkbox';

import { PromptSequenceIcons } from '../icons/icons';
import { Skeleton } from '../ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { MultiplePromptDisplay } from './multiple-prompt-display';
import { OutputPromptDispaly } from './output-prompt-display';

export const PromptCard = ({
  selectedPrompt,
  setSelectedPrompt,
  item,
  evalResults,
}: {
  selectedPrompt?: IPrompt[];
  setSelectedPrompt?: Dispatch<SetStateAction<IPrompt[]>>;
  item: IPrompt;
  evalResults?: EvalResults[];
}) => {
  const { messages } = useLanguage();
  const langData = messages?.General;
  const { state, evaluateActions } = useEvals();
  const { traceEvalLoading, evalTemplates } = state;

  const resolvedEvalResults = useMemo<EvalResults[]>(() => {
    if (evalResults && evalResults.length > 0) {
      return evalResults;
    }

    const fallbackResults = state.traceEvals?.results;
    if (!fallbackResults || fallbackResults.length === 0) {
      return [];
    }

    return fallbackResults
      .filter(
        (inferenceEval: EvalResults) =>
          inferenceEval.fact_id === `${item.trace_id}.${item.span_id}`
      )
      .filter(
        (inferenceEval: EvalResults) => inferenceEval.eval_found === true
      );
  }, [evalResults, state.traceEvals?.results, item.trace_id, item.span_id]);

  const isChecked = selectedPrompt?.some(
    (prompt) => prompt.span_id === item.span_id
  );

  const handleCheckedChange = (checked: boolean) => {
    if (checked) {
      setSelectedPrompt?.((prev) => [...prev, item]);
    } else {
      setSelectedPrompt?.((prev) =>
        prev.filter((prompt) => prompt.span_id !== item.span_id)
      );
    }
  };

  const start = new Date(item?.start_time).valueOf();
  const end = new Date(item?.end_time).valueOf();
  const duration = formatSecondsAndMilliseconds(end - start);

  const promptStatus = item['data.output']?.status;

  const { outputRole, outputString } = getRoleAndContent(item);

  return (
    <div
      className={`flex cursor-pointer gap-3 rounded border p-4 ${
        isChecked
          ? 'border-nav-selected-state bg-[#0A343C]'
          : 'border-line-tertiary bg-[#0A2D3A] hover:bg-[#0A343C]'
      }`}
    >
      <Checkbox
        checked={isChecked}
        onCheckedChange={handleCheckedChange}
        onClick={(e) => e.stopPropagation()}
        id={item?.span_id}
        className="h-[18px] w-[18px] border-form-control-border-rest"
      />
      <div className="mr-2 w-full">
        <div className="mb-3.5 flex justify-between">
          <div>
            <p className="text-form-input-text text-body-subtle">
              {langData?.duration}: {duration}
            </p>
            <p className="pt-[1px] text-body text-body-primary">
              {formatDateMonthTime(item?.start_time)}{' '}
              <span className="text-[11px]">{langData?.to}</span>{' '}
              {formatDateMonthTime(item?.end_time)}
            </p>
          </div>

          <div className="flex gap-6 pr-3.5">
            {item?.model_attributes?.service_name && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white">
                  <Image
                    className="size-6"
                    src={
                      iconMap[item?.model_attributes?.service_type] ??
                      iconMap['default']
                    }
                    alt={`${
                      item?.model_attributes?.service_type ?? 'default'
                    } provider`}
                    width={100}
                    height={100}
                  />
                </div>
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <p className="text-form-input-text font-normal text-form-input-disabled">
                        {langData?.service}
                      </p>
                      <TooltipTrigger>
                        <p className="max-w-[100px] truncate whitespace-nowrap pt-[1px] text-body text-body-primary [@media(min-width:1800px)]:max-w-full">
                          {item?.model_attributes?.service_name}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="rounded border-transparent bg-[#07181E] text-[12px] text-body-primary"
                      >
                        {item?.model_attributes?.service_name}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}

            {item?.model_attributes?.provider_name && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white">
                  <Image
                    className="size-6"
                    src={
                      iconMap[item?.model_attributes?.provider_type] ??
                      iconMap['default']
                    }
                    alt={`${
                      item?.model_attributes?.provider_type ?? 'default'
                    } provider`}
                    width={100}
                    height={100}
                  />
                </div>
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <p className="text-form-input-text font-normal text-form-input-disabled">
                        {langData?.provider}
                      </p>
                      <TooltipTrigger>
                        <p className="max-w-[100px] truncate whitespace-nowrap pt-[1px] text-body text-body-primary [@media(min-width:1800px)]:max-w-full">
                          {item?.model_attributes?.provider_name}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="rounded border-transparent bg-[#07181E] text-[12px] text-body-primary"
                      >
                        {item?.model_attributes?.provider_name}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}

            {item?.model_name && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white">
                  <Image
                    className="size-6"
                    src={iconMap['model.llm']}
                    alt={'model.llm icon'}
                    width={100}
                    height={100}
                  />
                </div>

                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <p className="text-form-input-text font-normal text-form-input-disabled">
                        {langData?.model}
                      </p>
                      <TooltipTrigger>
                        <p className="max-w-[100px] truncate whitespace-nowrap pt-[1px] text-body text-body-primary [@media(min-width:1800px)]:max-w-full">
                          {item?.model_name}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="rounded border-transparent bg-[#07181E] text-[12px] text-body-primary"
                      >
                        {item?.model_name}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex w-full rounded-sm border border-[#40565A]">
          {/* Input Section (40%) */}

          <ErrorBoundary
            customErrorMessage={messages?.ErrorBoundary.prompts_error || ''}
            className="w-2/5 px-2 py-3"
          >
            <div className="max-h-[262px] w-2/5 shrink-0 overflow-y-auto">
              <p className="mx-4 mt-3 text-form-input-text text-form-input-disabled">
                {langData?.input}:
              </p>
              {/* 3 Layers guard has been implemented to the input prompt data. */}
              <MultiplePromptDisplay inputData={item} />
            </div>
          </ErrorBoundary>

          {/* Output Section (60%) */}
          <ErrorBoundary
            customErrorMessage={messages?.ErrorBoundary.prompts_error || ''}
            className="w-2/5 px-2 py-3"
          >
            <div
              className={`max-h-[262px] w-3/5 overflow-y-auto border-l border-[#40565A] px-4 py-3 text-form-input-text ${promptStatus === 'error' ? 'bg-[#F26C7429]' : ''}`}
            >
              {promptStatus === 'error' ? (
                <div className="flex items-center gap-1">
                  {PromptSequenceIcons.ErrorStatusIcon(
                    'text-error-dark h-6 w-6'
                  )}
                  <p className="text-form-input-text font-normal text-error-dark ">
                    {langData?.error}
                  </p>
                </div>
              ) : (
                <p className="mb-1 text-form-input-disabled">
                  {langData?.output}:
                </p>
              )}
              <div className="mt-1 break-words break-all text-body-primary">
                {/* 3 Layers guard has been implemented to the output prompt data. */}

                <OutputPromptDispaly
                  outputRole={outputRole}
                  outputString={outputString}
                />
              </div>
              {traceEvalLoading ? (
                <Skeleton
                  className={cn(
                    'animate-pulse-custom w-16 rounded-full bg-[#8C9EA1]',
                    sizeStyles['default']
                  )}
                />
              ) : (
                resolvedEvalResults.map((inferenceEval: EvalResults) => (
                  <div
                    key={`${inferenceEval.fact_id}_${inferenceEval.eval_timestamp}`}
                    className="-ml-1 mt-2 w-fit"
                  >
                    <EvalBadge
                      evalObj={inferenceEval}
                      submitManualLabel={evaluateActions?.submitManualLabel}
                      createEvalsJobs={evaluateActions?.createEvalsJobs}
                      factName="inferences"
                      startTime={item.start_time}
                      endTime={item.end_time}
                      appName={item.app_name || ''}
                      workflowName={
                        inferenceEval.workflow_name ||
                        item.workflow_name ||
                        undefined
                      }
                      templateEnums={getTemplateLabelEnums(
                        evalTemplates,
                        inferenceEval.eval_name
                      )}
                      templateLoading={state.templateLoading}
                      sendEvalPromptToKahu={sendEvalPromptToKahu}
                    />
                  </div>
                ))
              )}
            </div>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};
